import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Plus, EnvelopeSimpleOpen } from '@phosphor-icons/react'
import { useNotifications } from '@/components/providers/NotificationProvider'
import { motion, AnimatePresence } from 'framer-motion'
import JoinByCodeSheet from '@/components/invite/JoinByCodeSheet'

const NOTIF_POPOVER_DISMISS_KEY = 'gunum-var-notif-popover-dismissed'

export default function BottomNav() {
  const navigate = useNavigate()
  const location = useLocation()
  const { unreadCount, permission, enableNotifications } = useNotifications()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isJoinSheetOpen, setIsJoinSheetOpen] = useState(false)
  const [showNotifPopover, setShowNotifPopover] = useState(false)
  const [notifLoading, setNotifLoading] = useState(false)

  useEffect(() => {
    // İzin henüz istenmemişse ve daha önce dismiss edilmemişse popover göster
    if (permission === 'default' && !localStorage.getItem(NOTIF_POPOVER_DISMISS_KEY)) {
      const timer = setTimeout(() => setShowNotifPopover(true), 1500)
      return () => clearTimeout(timer)
    } else {
      setShowNotifPopover(false)
    }
  }, [permission])

  const handleNotifEnable = async () => {
    setNotifLoading(true)
    try {
      await enableNotifications()
      setShowNotifPopover(false)
      // Başarılı olunca bir daha gösterme
      localStorage.setItem(NOTIF_POPOVER_DISMISS_KEY, 'true')
    } finally {
      setNotifLoading(false)
    }
  }

  const handleNotifDismiss = () => {
    setShowNotifPopover(false)
    localStorage.setItem(NOTIF_POPOVER_DISMISS_KEY, 'true')
  }

  // Function to determine if a path is active
  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/' || location.pathname === '/dashboard'
    return location.pathname.startsWith(path)
  }

  return (
    <>
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            onClick={() => setIsMenuOpen(false)}
            className="fixed inset-0 bg-[#FCFAF8]/90 z-[45]"
          />
        )}
      </AnimatePresence>

      {/* Bildirim popover backdrop — frosted glass */}
      <AnimatePresence>
        {showNotifPopover && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={handleNotifDismiss}
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[55]"
          />
        )}
      </AnimatePresence>

      <nav className={`fixed bottom-0 left-0 right-0 pt-3 pb-[max(env(safe-area-inset-bottom),_1rem)] bg-[#FCFAF8]/95 backdrop-blur-2xl border-t border-[#EDEDED] ${showNotifPopover ? 'z-[60]' : 'z-50'} flex items-center justify-between px-6 shadow-[0_-20px_25px_rgba(252,250,248,0.7)]`}>
        {/* Shared Animated Iconoir CSS */}
        <style>{`
                    .ai-primary { stroke: var(--animated-iconoir-primary, var(--ai-primary, currentColor)); }
                    .ai-secondary { stroke: var(--animated-iconoir-secondary, var(--ai-secondary, currentColor)); }
                    .ai-delay-0 { --ai-delay: 0ms; }
                    .ai-delay-1 { --ai-delay: 80ms; }
                    .ai-delay-2 { --ai-delay: 160ms; }
                    .ai-anim-scale-pop { transform-origin: center; }
                    .ai-icon-wrapper:hover .ai-anim-scale-pop,
                    .animated-iconoir:hover .ai-anim-scale-pop { animation: ai-scale-pop 500ms cubic-bezier(0.34, 1.56, 0.64, 1) var(--ai-delay, 0ms) both; }
                    @keyframes ai-scale-pop { 0% { transform: scale(1); } 40% { transform: scale(1.15); } 100% { transform: scale(1); } }
                    .ai-anim-fade { }
                    .ai-icon-wrapper:hover .ai-anim-fade,
                    .animated-iconoir:hover .ai-anim-fade { animation: ai-fade-pop 500ms ease var(--ai-delay, 0ms) both; }
                    @keyframes ai-fade-pop { 0% { opacity: 0.3; transform: scale(0.92); } 60% { opacity: 1; transform: scale(1.04); } 100% { opacity: 1; transform: scale(1); } }
                    .ai-anim-fill { fill: currentColor; fill-opacity: 0; transition: fill-opacity 500ms ease var(--ai-delay, 0ms); }
                    .ai-icon-wrapper:hover .ai-anim-fill { fill-opacity: 0.18; }
                    .ai-anim-bell-ring { transform-origin: 12px 3px; }
                    .ai-icon-wrapper:hover .ai-anim-bell-ring,
                    .animated-iconoir:hover .ai-anim-bell-ring { animation: ai-bell-ring 0.7s ease; }
                    @keyframes ai-bell-ring { 0% { transform: rotate(0deg); } 12% { transform: rotate(14deg); } 24% { transform: rotate(-12deg); } 36% { transform: rotate(8deg); } 48% { transform: rotate(-5deg); } 60% { transform: rotate(2deg); } 100% { transform: rotate(0deg); } }
                `}</style>

        {/* Ana Sayfa — Home (scale-pop + fade) */}
        <div
          className={`ai-icon-wrapper flex flex-col items-center gap-1.5 cursor-pointer w-14 ${isActive('/') ? 'text-[#D4AF37]' : 'text-[#707070]'}`}
          onClick={() => navigate('/dashboard')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            overflow="visible"
            className="animated-iconoir"
            role="img"
            aria-label="home"
          >
            <path
              d="M2 8L11.7317 3.13416C11.9006 3.04971 12.0994 3.0497 12.2683 3.13416L22 8"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="ai-primary ai-anim-scale-pop ai-delay-0"
            />
            <path
              d="M20 11V19C20 20.1046 19.1046 21 18 21H6C4.89543 21 4 20.1046 4 19V11"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="ai-secondary ai-anim-fade ai-delay-1"
            />
          </svg>
          <span className="text-[11px] font-bold">Ana Sayfa</span>
        </div>

        {/* Günlerim — Wallet (fill + fade) */}
        <div
          className={`ai-icon-wrapper flex flex-col items-center gap-1.5 cursor-pointer w-14 ${isActive('/groups') && !location.pathname.includes('/new') ? 'text-[#D4AF37]' : 'text-[#707070]'}`}
          onClick={() => navigate('/groups')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            overflow="visible"
            className="animated-iconoir"
            role="img"
            aria-label="wallet"
          >
            <path
              d="M19 20H5C3.89543 20 3 19.1046 3 18V9C3 7.89543 3.89543 7 5 7H19C20.1046 7 21 7.89543 21 9V18C21 19.1046 20.1046 20 19 20Z"
              stroke="currentColor"
              className="ai-primary ai-anim-fill ai-delay-0"
            />
            <path
              d="M16.5 14C16.2239 14 16 13.7761 16 13.5C16 13.2239 16.2239 13 16.5 13C16.7761 13 17 13.2239 17 13.5C17 13.7761 16.7761 14 16.5 14Z"
              fill="currentColor"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="ai-secondary ai-anim-fade ai-delay-1"
            />
            <path
              d="M18 7V5.60322C18 4.28916 16.7544 3.33217 15.4847 3.67075L4.48467 6.60409C3.60917 6.83756 3 7.63046 3 8.53656V9"
              stroke="currentColor"
              className="ai-secondary ai-anim-fade ai-delay-2"
            />
          </svg>
          <span className="text-[11px] font-bold">Günlerim</span>
        </div>

        {/* Floating Plus Button Area */}
        <div className="relative flex flex-col items-center justify-end w-[76px] pb-[-4px]">
          {/* Radial Menu Buttons */}
          <AnimatePresence>
            {isMenuOpen && (
              <>
                <motion.button
                  initial={{ opacity: 0, scale: 0.5, x: 0, y: 0 }}
                  animate={{ opacity: 1, scale: 1, x: -75, y: -90 }}
                  exit={{ opacity: 0, scale: 0.5, x: 0, y: 0 }}
                  transition={{ type: 'tween', ease: 'easeOut', duration: 0.2 }}
                  onClick={() => {
                    setIsMenuOpen(false)
                    navigate('/groups/new')
                  }}
                  className="absolute -top-[52px] flex flex-col items-center gap-2 z-20 outline-none transition-transform active:scale-95"
                  aria-label="Yeni Grup Kur"
                >
                  <div className="w-[60px] h-[60px] bg-gradient-to-br from-[#FFFDF5] to-[#FAFAFA] rounded-full shadow-[0_8px_20px_rgba(212,175,55,0.25)] flex items-center justify-center border-2 border-[#D4AF37]/40">
                    <Plus size={28} className="text-[#D4AF37]" weight="bold" />
                  </div>
                  <span className="text-[11px] font-black text-amber-950 bg-[#FCFAF8] px-3 py-1.5 rounded-full shadow-sm border border-[#D4AF37]/20 whitespace-nowrap">
                    Yeni Grup Oluştur
                  </span>
                </motion.button>

                <motion.button
                  initial={{ opacity: 0, scale: 0.5, x: 0, y: 0 }}
                  animate={{ opacity: 1, scale: 1, x: 75, y: -90 }}
                  exit={{ opacity: 0, scale: 0.5, x: 0, y: 0 }}
                  transition={{ type: 'tween', ease: 'easeOut', duration: 0.2, delay: 0.05 }}
                  onClick={() => {
                    setIsMenuOpen(false)
                    setIsJoinSheetOpen(true)
                  }}
                  className="absolute -top-[52px] flex flex-col items-center gap-2 z-20 outline-none transition-transform active:scale-95"
                  aria-label="Kodu Gir"
                >
                  <div className="w-[60px] h-[60px] bg-gradient-to-br from-[#FFFDF5] to-[#FAFAFA] rounded-full shadow-[0_8px_20px_rgba(212,175,55,0.25)] flex items-center justify-center border-2 border-[#D4AF37]/40">
                    <EnvelopeSimpleOpen size={28} className="text-[#D4AF37]" weight="bold" />
                  </div>
                  <span className="text-[11px] font-black text-amber-950 bg-[#FCFAF8] px-3 py-1.5 rounded-full shadow-sm border border-[#D4AF37]/20 whitespace-nowrap">
                    Davetiye Kodu Gir
                  </span>
                </motion.button>
              </>
            )}
          </AnimatePresence>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="absolute -top-[52px] w-[76px] h-[76px] bg-gradient-to-b from-[#F3CF6D] to-[#D4A91E] rounded-full shadow-[0_8px_20px_rgba(212,175,55,0.4)] flex items-center justify-center border-[6px] border-[#FCFAF8] transition-transform active:scale-95 z-30"
          >
            <motion.div
              animate={{ rotate: isMenuOpen ? 45 : 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <Plus size={36} className="text-[#1A1A1A]" strokeWidth={2.5} />
            </motion.div>
          </button>
          {/* The spacer ensures the YENİ GÜN text aligns horizontally with the adjacent nav texts (Ana Sayfa etc) */}
          <div className="h-[26px]"></div>
          <span
            className={`mt-1.5 text-[10.5px] font-bold uppercase tracking-wide ${isActive('/groups/new') || isMenuOpen ? 'text-[#D4AF37]' : 'text-[#707070]'}`}
          >
            YENİ GÜN
          </span>
        </div>

        {/* Bildirimler — Bell (bell-ring + fade) */}
        <div
          className={`ai-icon-wrapper flex flex-col items-center gap-1.5 cursor-pointer w-14 relative ${isActive('/notifications') ? 'text-[#D4AF37]' : 'text-[#707070]'}`}
          onClick={() => navigate('/notifications')}
        >
          {/* 🔔 Popover — Bildirim izni isteme */}
          <AnimatePresence>
            {showNotifPopover && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.9 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                className="fixed bottom-[100px] left-1/2 -translate-x-1/2 w-[280px] z-[60]"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.15),0_0_0_1px_rgba(0,0,0,0.05)] p-5">
                  <h4 className="text-[15px] font-extrabold text-[#1A1A1A] leading-tight">
                    Bildirimleri Aç
                  </h4>
                  <p className="text-[12.5px] text-[#6B7280] mt-1.5 leading-relaxed">
                    Ödeme hatırlatmaları ve grup güncellemelerinden anında haberdar ol.
                  </p>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={handleNotifEnable}
                      disabled={notifLoading}
                      className="flex-1 bg-gradient-to-r from-[#D4AF37] to-[#C9A02E] hover:from-[#C9A02E] hover:to-[#B8922C] text-white text-[13px] font-bold py-2.5 rounded-xl active:scale-95 transition-all disabled:opacity-60 shadow-sm"
                    >
                      {notifLoading ? (
                        <span className="flex items-center justify-center gap-1.5">
                          <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Bağlanıyor
                        </span>
                      ) : (
                        'Bildirimleri Aç'
                      )}
                    </button>
                    <button
                      onClick={handleNotifDismiss}
                      className="px-4 py-2.5 text-[13px] font-bold text-[#9CA3AF] hover:text-[#6B7280] hover:bg-gray-50 rounded-xl transition-colors"
                    >
                      Sonra
                    </button>
                  </div>
                </div>
                {/* Arrow — bell ikonuna doğru sağa kaydırılmış */}
                <div className="flex justify-end pr-[60px] -mt-[1px]">
                  <div className="w-4 h-4 bg-white rotate-45 -mt-2 shadow-[2px_2px_4px_rgba(0,0,0,0.05)]" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="relative">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              overflow="visible"
              className="animated-iconoir"
              role="img"
              aria-label="bell"
            >
              <path
                d="M18 8.4C18 6.70261 17.3679 5.07475 16.2426 3.87452C15.1174 2.67428 13.5913 2 12 2C10.4087 2 8.88258 2.67428 7.75736 3.87452C6.63214 5.07475 6 6.70261 6 8.4C6 15.8667 3 18 3 18H21C21 18 18 15.8667 18 8.4Z"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="ai-primary ai-anim-bell-ring ai-delay-0"
              />
              <path
                d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="ai-secondary ai-anim-fade ai-delay-1"
              />
            </svg>
            {unreadCount > 0 && (
              <span className="absolute -top-1.5 -right-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold leading-none text-white bg-red-500 rounded-full border-2 border-[#FCFAF8]">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>
          <span className="text-[11px] font-bold">Bildirimler</span>
        </div>

        {/* Profil — User (fade + fade) */}
        <div
          className={`ai-icon-wrapper flex flex-col items-center gap-1.5 cursor-pointer w-14 ${isActive('/profile') ? 'text-[#D4AF37]' : 'text-[#707070]'}`}
          onClick={() => navigate('/profile')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            overflow="visible"
            className="animated-iconoir"
            role="img"
            aria-label="user"
          >
            <path
              d="M5 20V19C5 15.134 8.13401 12 12 12V12C15.866 12 19 15.134 19 19V20"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="ai-primary ai-anim-fade ai-delay-0"
            />
            <path
              d="M12 12C14.2091 12 16 10.2091 16 8C16 5.79086 14.2091 4 12 4C9.79086 4 8 5.79086 8 8C8 10.2091 9.79086 12 12 12Z"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="ai-secondary ai-anim-fade ai-delay-1"
            />
          </svg>
          <span className="text-[11px] font-bold">Profil</span>
        </div>
      </nav>

      <JoinByCodeSheet isOpen={isJoinSheetOpen} onClose={() => setIsJoinSheetOpen(false)} />
    </>
  )
}
